import { API_BASE_URL } from "../config/api";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

function AdminTravel() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [complaintMap, setComplaintMap] = useState({});
  const [customerMap, setCustomerMap] = useState({});

  const fetchData = async (query = "") => {
    try {
      setLoading(true);
      const [travelRes, complaintsRes, customersRes] = await Promise.all([
        axios.get(API_BASE_URL + "/api/travel", {
          params: query ? { search: query } : undefined
        }),
        axios.get(API_BASE_URL + "/api/complaints"),
        axios.get(API_BASE_URL + "/api/customers")
      ]);

      const complaints = Array.isArray(complaintsRes.data) ? complaintsRes.data : [];
      const customers = Array.isArray(customersRes.data) ? customersRes.data : [];

      const nextComplaintMap = complaints.reduce((acc, item) => {
        [item._id, item.complaintId]
          .filter(Boolean)
          .map(String)
          .forEach((key) => {
            acc[key] = item;
          });
        return acc;
      }, {});

      const nextCustomerMap = customers.reduce((acc, item) => {
        acc[String(item._id)] = item;
        return acc;
      }, {});

      setComplaintMap(nextComplaintMap);
      setCustomerMap(nextCustomerMap);
      setData(
        (Array.isArray(travelRes.data) ? travelRes.data : [])
          .filter((item) => item.entryType === "complaint")
      );
    } catch (err) {
      console.error("Error fetching travel data:", err);
      setData([]);
      setComplaintMap({});
      setCustomerMap({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const rows = useMemo(() => {
    return data
      .map((travel) => {
        const complaint =
          complaintMap[String(travel.complaintId || "")] ||
          complaintMap[String(travel.complaintRefId || "")] ||
          null;
        const customer = customerMap[String(travel.customerId || "")] || null;

        return {
          id: travel._id,
          engineer: travel.engineerName || travel.engineerId || "-",
          complaintId: complaint?.complaintId || travel.complaintRefId || travel.complaintId || "-",
          complaintDate: formatDate(complaint?.createdAt || travel.createdAt),
          customer: customer?.name || travel.company || complaint?.company || "-",
          city: travel.city || customer?.address?.city || "-",
          status: travel.status || "Not Started",
          startTime: formatDateTime(travel.journey?.startTime),
          reachSiteTime: formatDateTime(travel.journey?.reachSiteTime),
          leaveSiteTime: formatDateTime(travel.journey?.leaveSiteTime),
          reachHomeTime: formatDateTime(travel.journey?.reachHomeTime),
          primaryTime: formatDateTime(
            travel.journey?.reachHomeTime ||
            travel.journey?.leaveSiteTime ||
            travel.journey?.reachSiteTime ||
            travel.journey?.startTime ||
            travel.updatedAt ||
            travel.createdAt
          ),
          latestMilestone: getLatestMilestone(travel),
          timings: buildTimingDetails(travel),
          googleMapsLink: travel.googleMapsLink || "",
          customerId: travel.customerId || ""
        };
      })
      .sort((a, b) => {
        const aTime = parseDateTime(data.find((item) => item._id === a.id)?.createdAt);
        const bTime = parseDateTime(data.find((item) => item._id === b.id)?.createdAt);
        return bTime - aTime;
      });
  }, [complaintMap, customerMap, data]);

  const visibleRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return rows;

    return rows.filter((row) =>
      [
        row.engineer,
        row.complaintId,
        row.complaintDate,
        row.customer,
        row.city,
        row.status,
        row.latestMilestone
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [rows, search]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    fetchData(search);
  };

  const handleSaveLocationToCustomer = async (customerId, googleMapsLink) => {
    try {
      const customer = customerMap[customerId];
      if (!customer) {
        alert("Customer details not found on this page.");
        return;
      }
      const confirmed = window.confirm(`Are you sure you want to save this location link to the customer profile of "${customer.name}"?`);
      if (!confirmed) return;

      await axios.put(API_BASE_URL + `/api/customers/${customerId}`, {
        ...customer,
        googleMapsLink
      });

      alert("Location link successfully saved to customer profile!");
      fetchData(search);
    } catch (error) {
      console.error("Error saving customer location:", error);
      alert(error.response?.data?.message || "Failed to save location to customer profile.");
    }
  };

  return (
    <div className="mobile-dense-page mx-auto max-w-7xl space-y-4 lg:space-y-5">
      <div className="hidden flex-col gap-3 lg:flex lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Travel Monitoring</h1>
        </div>

        <form onSubmit={handleSearchSubmit} className="flex w-full gap-2 lg:w-auto">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search engineer, complaint, customer..."
            className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm lg:w-96"
          />
          <button
            type="submit"
            className="rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 sm:px-5"
          >
            Search
          </button>
        </form>
      </div>

      <div className="space-y-4 lg:hidden">
        <div className="rounded-2xl bg-slate-950 px-3 py-4 text-white">
          <h1 className="text-xl font-bold">Travel Monitoring</h1>
          <form onSubmit={handleSearchSubmit} className="mt-4 flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-slate-900">
            <TravelIcon type="search" className="h-4 w-4 shrink-0 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search travel"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
            <button
              type="submit"
              className="rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white"
            >
              Search
            </button>
          </form>
        </div>

        {loading ? (
          <div className="rounded-2xl bg-slate-900 p-6 text-center text-slate-400 ring-1 ring-white/5">Loading travel data...</div>
        ) : visibleRows.length === 0 ? (
          <div className="rounded-2xl bg-slate-900 p-6 text-center text-slate-400 ring-1 ring-white/5">No travel data found</div>
        ) : (
          visibleRows.map((row) => (
            <TravelCard
              key={row.id}
              row={row}
              onSaveLocation={handleSaveLocationToCustomer}
            />
          ))
        )}
      </div>

      <div className="hidden overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm lg:block">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading travel data...</div>
        ) : visibleRows.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No travel data found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1180px]">
              <thead className="bg-gray-100">
                <tr className="text-left text-sm font-semibold text-gray-700">
                  <th className="px-4 py-4">Engineer</th>
                  <th className="px-4 py-4">Complaint ID</th>
                  <th className="px-4 py-4">Date</th>
                  <th className="px-4 py-4">Customer Name</th>
                  <th className="px-4 py-4">City</th>
                  <th className="px-4 py-4">GPS Location</th>
                  <th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4">Started</th>
                  <th className="px-4 py-4">Reached Site</th>
                  <th className="px-4 py-4">Left Site</th>
                  <th className="px-4 py-4">Reached Home</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.id} className="border-t text-sm text-gray-700 hover:bg-gray-50">
                    <td className="px-4 py-4 font-semibold text-gray-900">{row.engineer}</td>
                    <td className="px-4 py-4 font-medium text-gray-900">{row.complaintId}</td>
                    <td className="px-4 py-4">{row.complaintDate}</td>
                    <td className="px-4 py-4">{row.customer}</td>
                    <td className="px-4 py-4">{row.city}</td>
                    <td className="px-4 py-4 font-semibold">
                      {row.googleMapsLink ? (
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center">
                          <a
                            href={row.googleMapsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-bold text-sky-600 hover:text-sky-800 hover:underline"
                          >
                            View Map
                          </a>
                          {row.customerId && (
                            <button
                              type="button"
                              onClick={() => handleSaveLocationToCustomer(row.customerId, row.googleMapsLink)}
                              className="rounded bg-sky-50 px-1.5 py-0.5 text-[9px] font-extrabold text-sky-700 hover:bg-sky-100 border border-sky-200"
                              title="Save this location to the customer's profile"
                            >
                              Save
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 font-normal">Not Captured</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-4 py-4">{row.startTime}</td>
                    <td className="px-4 py-4">{row.reachSiteTime}</td>
                    <td className="px-4 py-4">{row.leaveSiteTime}</td>
                    <td className="px-4 py-4">{row.reachHomeTime}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TravelCard({ row, onSaveLocation }) {
  return (
    <article className={`overflow-hidden rounded-2xl bg-slate-900 shadow-sm ring-1 ring-white/5 ${getCardAccent(row.status)}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-800 text-slate-200">
              <TravelIcon type={getStatusIcon(row.status)} className="h-5 w-5 text-slate-200" />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-lg font-bold text-slate-100">{row.engineer}</h2>
              <p className="mt-0.5 text-sm font-semibold text-slate-400">ID: {row.complaintId}</p>
            </div>
          </div>
          <StatusBadge status={row.status} />
        </div>

        <div className="mt-4 space-y-3">
          <TravelDetail icon="calendar" label={row.status === "Completed" ? "Service Date" : "Job Date"} value={row.complaintDate} />
          <TravelDetail icon="clock" label="Latest Action" value={row.latestMilestone} />
          <TravelDetail icon="building" label="Customer Name" value={row.customer} subValue={row.city} />
          {row.googleMapsLink ? (
            <div className="flex min-w-0 gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-slate-800">
                <TravelIcon type="pin" className="h-4 w-4 text-sky-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">GPS Location</p>
                <div className="mt-1 flex items-center gap-2">
                  <a
                    href={row.googleMapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-bold text-sky-400 hover:text-sky-300 hover:underline"
                  >
                    Open Map
                  </a>
                  {row.customerId && onSaveLocation && (
                    <button
                      type="button"
                      onClick={() => onSaveLocation(row.customerId, row.googleMapsLink)}
                      className="rounded bg-sky-950 px-2 py-0.5 text-[10px] font-bold text-sky-300 ring-1 ring-sky-800 transition hover:bg-sky-900"
                    >
                      Save to Profile
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <details className="mt-4 rounded-xl bg-slate-950/70 ring-1 ring-white/5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-slate-200">
            <span>Timing Details</span>
            <span className="text-blue-300">Open</span>
          </summary>
          <div className="border-t border-white/10 px-3 py-3">
            <div className="space-y-2">
              {row.timings.map((item) => (
                <div key={item.label} className="flex items-start justify-between gap-3 rounded-lg bg-slate-900 px-3 py-2 text-sm">
                  <span className="font-semibold text-slate-300">{item.label}</span>
                  <span className={`text-right ${item.value === "--" ? "text-slate-500" : "font-semibold text-slate-50"}`}>
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </details>
      </div>
    </article>
  );
}

function TravelDetail({ icon, label, value, subValue }) {
  return (
    <div className="flex min-w-0 gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-slate-800">
        <TravelIcon type={icon} className="h-4 w-4 text-slate-300" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">{label}</p>
        <p className="mt-0.5 break-words text-base font-semibold leading-5 text-slate-100">{value || "--"}</p>
        {subValue ? <p className="mt-1 text-sm text-slate-300">{subValue}</p> : null}
      </div>
    </div>
  );
}

function TravelIcon({ type, className = "h-4 w-4 shrink-0 text-gray-500" }) {
  const paths = {
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.35-4.35" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="4" width="18" height="17" rx="2" />
        <path d="M8 2v4M16 2v4M3 10h18" />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    pin: (
      <>
        <path d="M12 21s7-4.4 7-11a7 7 0 10-14 0c0 6.6 7 11 7 11z" />
        <circle cx="12" cy="10" r="2.2" />
      </>
    ),
    building: (
      <>
        <path d="M4 21V7a2 2 0 012-2h7a2 2 0 012 2v14" />
        <path d="M15 10h3a2 2 0 012 2v9M8 9h3M8 13h3M8 17h3" />
      </>
    ),
    user: (
      <>
        <path d="M20 21a8 8 0 00-16 0" />
        <circle cx="12" cy="7" r="4" />
      </>
    ),
    car: (
      <>
        <path d="M3 13l2-5a3 3 0 012.8-2h8.4A3 3 0 0119 8l2 5" />
        <path d="M5 13h14v5H5zM7 18v2M17 18v2" />
      </>
    ),
    check: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M8 12l2.5 2.5L16 9" />
      </>
    ),
  };

  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {paths[type] || paths.pin}
    </svg>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-white sm:text-xs ${getStatusColor(status)}`}>
      <span className="mr-1.5 mt-[0.4em] h-1.5 w-1.5 rounded-full bg-white/80" />
      {status || "Not Started"}
    </span>
  );
}

const formatDate = (value) => {
  if (!value) return "--";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "--";
  return parsed.toLocaleDateString("en-GB");
};

const formatDateTime = (time) => {
  if (!time) return "--";
  const parsed = new Date(time);
  if (Number.isNaN(parsed.getTime())) return "--";
  return parsed.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};

const formatTime = (time) => {
  if (!time) return "";
  const parsed = new Date(time);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};

const buildTimingDetails = (travel) => {
  const journey = travel.journey || {};
  return [
    { label: "Started Journey", value: formatDateTime(journey.startTime) },
    { label: "Reached Site", value: formatDateTime(journey.reachSiteTime) },
    { label: "Left Site", value: formatDateTime(journey.leaveSiteTime) },
    { label: "Reached Home", value: formatDateTime(journey.reachHomeTime) }
  ];
};

const getLatestMilestone = (travel) => {
  const journey = travel.journey || {};
  if (journey.reachHomeTime) return `Reached Home, ${formatTime(journey.reachHomeTime)}`;
  if (journey.leaveSiteTime) return `Left Site, ${formatTime(journey.leaveSiteTime)}`;
  if (journey.reachSiteTime) return `Reached Site, ${formatTime(journey.reachSiteTime)}`;
  if (journey.startTime) return `Started, ${formatTime(journey.startTime)}`;
  return "--";
};

const parseDateTime = (value) => {
  const parsed = new Date(value || 0).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getStatusColor = (status) => {
  switch (status) {
    case "Started":
      return "bg-blue-500";
    case "At Site":
      return "bg-amber-500";
    case "Left Site":
      return "bg-violet-500";
    case "Completed":
      return "bg-emerald-600";
    default:
      return "bg-gray-400";
  }
};

const getCardAccent = (status) => {
  switch (status) {
    case "Started":
      return "border-b-4 border-blue-500";
    case "At Site":
      return "border-b-4 border-red-600";
    case "Left Site":
      return "border-b-4 border-violet-500";
    case "Completed":
      return "border-b-4 border-cyan-500";
    default:
      return "border-b-4 border-slate-600";
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case "Started":
      return "car";
    case "Completed":
      return "check";
    default:
      return "user";
  }
};

export default AdminTravel;
